/**
 * Cockpit-Mode-Helpers (TQ #6906 Refactor 2026-06-03).
 *
 * Zentrale Definition welche Hosts welcher Mode sind, sodass die Org-Pages
 * (Tickets, Downloads, Services, Billing, Settings, Licenses) als EINE
 * Codebase im Personal- + Org-Mode laufen können — kein Doppel-Code, keine
 * parallelen PersonalXxx-Varianten.
 */

import { isPersonalMode as configIsPersonalMode } from '../lib/runtimeConfig'

// Seit TQ #409 entscheidet die Laufzeit-Config (mode=personal), nicht der Hostname.
export function isPersonalMode(): boolean {
  if (typeof window === 'undefined') return false
  return configIsPersonalMode()
}

/**
 * Baut die API-Base je nach Mode für eine Org-vs-Personal-Endpoint-Paar-Strategie.
 *
 *   personalMode  → '/api'                              (z.B. /api/subscriptions)
 *   org/staff      → '/api/org/<token>' (Backend-Slug)  (z.B. /api/org/:slug/services)
 *
 * Pages sollten die individuellen Suffix-Routen wissen, damit sie nicht
 * blind Url-Strings konkatenieren — wir haben ja schon Unterschiede in den
 * Endpoint-Namen (services vs subscriptions etc.). Helper ist daher
 * dünn-mappend und überlässt der Page das Pfad-Suffix.
 */
export function apiBase(orgRef?: { public_token?: string; slug?: string }): string {
  if (isPersonalMode()) return '/api'
  if (!orgRef) return '/api'
  return `/api/org/${orgRef.public_token || orgRef.slug}`
}

/** Org-Referenz für modeAwareEndpoint: Org-Objekt (public_token/slug) oder roher Slug-String. */
export type OrgRef = { public_token?: string; slug?: string } | string | null | undefined

/**
 * Baut den vollen Endpoint-Pfad für ein Personal-vs-Org-Endpoint-PAAR mit
 * IDENTISCHEM Suffix (TQ #7348, Bundle #7343 "Personal-Mode Per-User-Filter").
 *
 *   personalMode  → `/api/me/<suffix>`           (Per-User-Filter aus JWT, z.B. /api/me/services)
 *   org/staff      → `/api/org/<token>/<suffix>`  (Org-scoped, z.B. /api/org/:slug/services)
 *
 * Ersetzt das über die Cockpit-Pages verstreute Inline-Ternary
 * `isPersonalMode() ? '/api/me/x' : '/api/org/${slug}/x'`. Nur für Endpoint-Paare
 * mit gleichem Suffix verwenden — wo Personal- und Org-Suffix divergieren
 * (z.B. Downloads products↔catalog, Billing-Legacy /api/invoices) NICHT geeignet.
 *
 * DSGVO: Der Personal-Pfad trägt KEINEN Identifier — das Backend filtert
 * ausschließlich über den verifizierten JWT-Sub, nie über einen Client-Param.
 *
 * @param suffix  Pfad nach dem Mode-Präfix, mit oder ohne führenden `/` (z.B. "services", "billing/invoices").
 * @param orgRef  Org-Objekt oder Slug-String. Im Personal-Mode ignoriert.
 */
export function modeAwareEndpoint(suffix: string, orgRef?: OrgRef): string {
  const clean = suffix.replace(/^\/+/, '')
  if (isPersonalMode()) return `/api/me/${clean}`
  const token = typeof orgRef === 'string' ? orgRef : orgRef?.public_token || orgRef?.slug
  return `/api/org/${token}/${clean}`
}
